import { allowedOriginsConfigJsonSchema, ThemeName } from './schemas';
import { Platform } from './types';

import { isIframeWindow } from '@/shared/utils/utils';

export const DEFAULT_THEME_NAME: ThemeName = 'default';

interface PlatformTerminologies {
  adUnit: Record<Platform['name'], { singular: string; plural: string }>;
  breadcrumbLabels: Record<
    Platform['name'],
    Record<string, string> | undefined
  >;
}

export const platformTerminologies: PlatformTerminologies = {
  adUnit: {
    'The Trade Desk': {
      singular: 'ad group',
      plural: 'ad groups',
    },
    Meta: {
      singular: 'ad set',
      plural: 'ad sets',
    },
    DV360: {
      singular: 'ad group',
      plural: 'ad groups',
    },
    TikTok: {
      singular: 'ad group',
      plural: 'ad groups',
    },
  },
  breadcrumbLabels: {
    'The Trade Desk': {
      'ad-unit': 'ad group',
    },
    Meta: {
      'ad-unit': 'ad set',
    },
    DV360: {
      'ad-unit': 'ad group',
    },
    TikTok: {
      'ad-unit': 'ad group',
    },
  },
};

const allowedOriginsConfigEnv = import.meta.env
  .VITE_IFRAME_ALLOWED_ORIGINS_CONFIG;
export const allowedOriginsConfig = allowedOriginsConfigJsonSchema.parse(
  allowedOriginsConfigEnv
);
export const iframeAllowedOrigins = Object.entries(allowedOriginsConfig).map(
  ([origin, config]) => {
    return `${config.protocol}://${origin}`;
  }
);

export function isAllowedIframeHost(
  hostUrl: string,
  allowedHosts: string[],
  allowedOriginsConfiguration: typeof allowedOriginsConfig
) {
  let normalizedHostUrl: URL;
  try {
    normalizedHostUrl = new URL(hostUrl);
  } catch (err) {
    console.error('Invalid URL provided to isAllowedIframeHost:', err);
    return false;
  }

  return allowedHosts.some((allowedHost) => {
    if (allowedHost === '*') {
      return true;
    }

    let normalizedAllowedHostUrl: URL;
    try {
      normalizedAllowedHostUrl = new URL(allowedHost);
    } catch (err) {
      console.error(
        'Invalid allowed host URL provided to isAllowedIframeHost:',
        err
      );
      return false;
    }

    const allowSubdomains =
      allowedOriginsConfiguration[normalizedAllowedHostUrl.host]
        .allowSubdomains;

    if (allowSubdomains) {
      return (
        normalizedHostUrl.origin === normalizedAllowedHostUrl.origin ||
        (normalizedHostUrl.origin.endsWith(
          `.${normalizedAllowedHostUrl.host}`
        ) &&
          normalizedHostUrl.protocol === normalizedAllowedHostUrl.protocol)
      );
    }

    return normalizedHostUrl.origin === normalizedAllowedHostUrl.origin;
  });
}

export function getDefaultThemeName(): ThemeName {
  // referer may not be present in some cases due to browser privacy settings
  // but still we can try to determine the theme based on referrer if our app is in an iframe
  // to avoid flash of default theme before applying the correct theme
  if (!isIframeWindow() || !document.referrer) {
    return 'default';
  }

  // In iframe, check the referrer to determine the theme
  let iframeHostUrl: URL;
  try {
    iframeHostUrl = new URL(document.referrer);
  } catch (err) {
    console.error('Invalid referrer URL in iframe:', err);
    return 'default';
  }

  const host = allowedOriginsConfig[iframeHostUrl.host];
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (host) {
    return host.themeName;
  }

  return 'default';
}
